import React, { ReactNode, useEffect, useState } from "react";
import { Box, Checkbox, FormControl, Label, TextInput } from "@primer/react";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import { chamSocKhachHangApi } from "../../../api/chamSocKhachHangApi";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonContext } from "../../../contexts/common";
import { ts_chamsockhachhang } from "../../../models/response/cskh/ts_chamsockhachhang";
import Modal from "../../ui/modal";
import Button from "../../ui/button";
import { sectionLabelSx, textareaSx } from "../../../pages/crm-survey/KhoLead/khoLeadStyles";
import { phuHuynhApi } from "../../../api/phuHuynhApi";
import SelectBoxLoaiLienHe from "../../selectbox-loai-lien-he";
import StyledDatePicker from "../../react-datepicker-style/ReactDatePicker";
import { useLoaiLienHe } from "../../selectbox-loai-lien-he/useLoaiLienHe";
import SelectBoxKetQuaLienHe from "../../selectbox-ket-qua-lien-he";
import { useKetQuaLienHe } from "../../selectbox-ket-qua-lien-he/useKetQuaLienHe";

interface PhuHuynhCSKHDetailModalProps {
  id: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const defaultValues: ts_chamsockhachhang = {
  id: 0,
  ten_khach_hang: "",
  dien_thoai: "",
  noi_dung: "",
  da_xu_ly: false,
  is_request: false,
  ts_hocsinh_id: 0,
  user_xu_ly_id: 0,
  ts_phuhuynh_id: 0,
  ts_hoso_survey_id: 0,
  ngay_xu_ly: null,
  ngay_hen_tra_loi: null,
  ket_qua_xu_ly: "",
  ghi_chu: "",
};

const TEXTAREA_MIN_HEIGHT = "84px";

const toDateOrNull = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.toDate() : null;
};

const toDatePayloadOrNull = (value?: string | Date | null) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <Box sx={{ ...sectionLabelSx, pb: 2, borderBottom: "1px solid", borderColor: "border.muted" }}>{title}</Box>
    {children}
  </Box>
);

const TwoColumns = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: ["1fr", "1fr 1fr"], gap: 3, alignItems: "start" }}>
    {children}
  </Box>
);

interface TextAreaProps {
  name: string;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur: () => void;
  ariaLabel: string;
  hasError?: boolean;
  minHeight?: string;
  grow?: boolean;
}

const TextArea = ({ name, value, onChange, onBlur, ariaLabel, hasError, minHeight = TEXTAREA_MIN_HEIGHT, grow }: TextAreaProps) => (
  <Box as="textarea"
    name={name}
    value={value ?? ""}
    onBlur={onBlur}
    onChange={(e: any) => onChange(e.target.value)}
    aria-label={ariaLabel}
    sx={{
      ...textareaSx,
      minHeight,
      ...(grow && { flex: 1, resize: "vertical" }),
      borderColor: hasError ? "danger.emphasis" : textareaSx.borderColor,
    }}
  />
);

interface DateInputFieldProps {
  name: string;
  value?: string | Date | null;
  onChange: (value: Date | null) => void;
  onBlur: () => void;
  ariaLabel: string;
  error?: string;
}

const DateInputField = ({ name, value, onChange, onBlur, ariaLabel, error }: DateInputFieldProps) => (
  <StyledDatePicker
    name={name}
    selected={toDateOrNull(value)}
    onChange={(date: Date | null) => onChange(date)}
    onBlur={onBlur}
    aria-label={ariaLabel}
    dateFormat="dd/MM/yyyy"
    showTimeSelect={false}
    placeholderText="dd/mm/yyyy"
    error={error}
  />
);

const PhuHuynhCSKHDetailModal: React.FC<PhuHuynhCSKHDetailModalProps> = ({ id, onCancel, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { translate } = useCommonContext();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<ts_chamsockhachhang>({ defaultValues });

  const daXuLy = watch("da_xu_ly");
  const phuHuynhId = watch("ts_phuhuynh_id");
  const [dmCosoId, setDmCosoId] = useState<number>();
  const loaiLienHes = useLoaiLienHe(dmCosoId);
  const ketQuaLienHes = useKetQuaLienHe(dmCosoId);
  const loaiId = watch("dm_loai_lien_he_id");
  const ketQuaId = watch("dm_ket_qua_lien_he_id");
  // Cần ngày hẹn khi loại liên hệ hoặc kết quả liên hệ được cấu hình "Cần ngày hẹn", giống Kho Lead
  const needNgayHen = !!loaiLienHes.find(x => x.id === loaiId)?.co_ngay_hen
    || !!ketQuaLienHes.find(x => x.id === ketQuaId)?.co_ngay_hen;

  // Cơ sở của phụ huynh, dùng để lọc danh sách loại liên hệ
  useEffect(() => {
    if (!phuHuynhId) return;
    phuHuynhApi.getById(phuHuynhId).then((ph: any) => {
      if (ph.is_success) setDmCosoId(ph.data?.dm_coso_id);
    });
  }, [phuHuynhId]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const res = await chamSocKhachHangApi.detail(id);
      if (res.is_success && res.data) {
        reset(res.data);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: ts_chamsockhachhang) => {
    try {
      setIsLoading(true);

      const requestData: ts_chamsockhachhang = {
        ...data,
        ts_hocsinh_id: data.ts_hocsinh_id || 0,
        user_xu_ly_id: data.user_xu_ly_id || 0,
        ts_phuhuynh_id: data.ts_phuhuynh_id || 0,
        ts_hoso_survey_id: data.ts_hoso_survey_id || 0,
        ngay_xu_ly: toDatePayloadOrNull(data.ngay_xu_ly),
        ngay_hen_tra_loi: (!data.dm_loai_lien_he_id && !data.dm_ket_qua_lien_he_id) || needNgayHen ? toDatePayloadOrNull(data.ngay_hen_tra_loi) : null,
      };

      const res = await chamSocKhachHangApi.update(requestData);

      if (res.is_success) {
        NotifyHelper.Success("Cập nhật thành công");
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || "Cập nhật không thành công");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi cập nhật");
    } finally {
      setIsLoading(false);
    }
  };

  const fieldError = (msg?: string) => msg ? (
    <FormControl.Validation variant="error">{msg}</FormControl.Validation>
  ) : null;

  const renderFooter = () => (
    <Box sx={{
      display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2,
      p: 3, borderTop: "1px solid", borderColor: "border.default", flexShrink: 0,
    }}>
      <Button text="Đóng" onClick={onCancel} disabled={isLoading} />
      <Button
        text={id > 0 ? "Cập nhật" : "Thêm mới"}
        variant="primary"
        isLoading={isLoading}
        disabled={!isDirty}
        onClick={handleSubmit(onSubmit)}
      />
    </Box>
  );

  return (
    <Modal
      isOpen
      title={id > 0 ? "Cập nhật yêu cầu" : "Thêm mới yêu cầu"}
      onClose={onCancel}
      renderFooter={renderFooter}
      sx={{
        width: ["95vw", "92vw", "980px"],
        maxWidth: "96vw",
      }}
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{
        display: "grid",
        gap: 4,
        alignItems: "stretch",
        gridTemplateColumns: ["1fr", "1fr", "7fr 5fr"],
      }}>
        <Section title={translate("Thông tin")}>
          <TwoColumns>
            <Controller
              name="ten_khach_hang"
              control={control}
              rules={{ required: "Vui lòng nhập tên khách hàng" }}
              render={({ field }) => (
                <FormControl>
                  <FormControl.Label>{translate("Tên khách hàng")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
                  <TextInput {...field} block aria-label="Tên khách hàng" validationStatus={errors.ten_khach_hang ? "error" : undefined} />
                  {fieldError(errors.ten_khach_hang?.message)}
                </FormControl>
              )}
            />
            <Controller
              name="dien_thoai"
              control={control}
              rules={{
                required: "Vui lòng nhập số điện thoại",
                pattern: { value: /^[0-9]{10,11}$/, message: "Số điện thoại không hợp lệ" },
              }}
              render={({ field }) => (
                <FormControl>
                  <FormControl.Label>{translate("Điện thoại")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
                  <TextInput {...field} block aria-label="Điện thoại" validationStatus={errors.dien_thoai ? "error" : undefined} />
                  {fieldError(errors.dien_thoai?.message)}
                </FormControl>
              )}
            />
            <Controller
              name="dm_loai_lien_he_id"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControl>
                  <FormControl.Label>{translate("Loại liên hệ")}</FormControl.Label>
                  <SelectBoxLoaiLienHe
                    loaiLienHes={loaiLienHes}
                    value={value}
                    onValueChanged={(v) => onChange(v ?? null)}
                    width="100%"
                  />
                </FormControl>
              )}
            />
            <Controller
              name="dm_ket_qua_lien_he_id"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControl>
                  <FormControl.Label>{translate("Kết quả liên hệ")}</FormControl.Label>
                  <SelectBoxKetQuaLienHe
                    ketQuaLienHes={ketQuaLienHes}
                    value={value}
                    onValueChanged={(v) => onChange(v ?? null)}
                    width="100%"
                  />
                </FormControl>
              )}
            />
            {needNgayHen && (
              <Controller
                name="ngay_hen_tra_loi"
                control={control}
                rules={{ validate: (v) => !needNgayHen || !!v || "Loại liên hệ này cần chọn ngày hẹn" }}
                render={({ field }) => (
                  <FormControl>
                    <FormControl.Label>{translate("Ngày hẹn trả lời")}</FormControl.Label>
                    <DateInputField
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ariaLabel="Ngày hẹn trả lời"
                      error={errors.ngay_hen_tra_loi?.message}
                    />
                  </FormControl>
                )}
              />
            )}
          </TwoColumns>
          <Controller
            name="noi_dung"
            control={control}
            rules={{ required: "Vui lòng nhập nội dung" }}
            render={({ field }) => (
              <FormControl>
                <FormControl.Label>{translate("Nội dung")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
                <TextArea
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ariaLabel="Nội dung"
                  minHeight="110px"
                  hasError={!!errors.noi_dung}
                />
                {fieldError(errors.noi_dung?.message)}
              </FormControl>
            )}
          />
          <Controller
            name="ghi_chu"
            control={control}
            render={({ field }) => (
              <FormControl>
                <FormControl.Label>{translate("Ghi chú")}</FormControl.Label>
                <TextArea
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ariaLabel="Ghi chú"
                  minHeight="80px"
                />
              </FormControl>
            )}
          />
        </Section>

        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: 3,
          bg: "canvas.subtle",
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "border.muted",
          }}>
            <Box sx={sectionLabelSx}>{translate("Xử lý")}</Box>
            <Controller
              name="da_xu_ly"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box
                  as="label"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />
                  <Label variant={value ? "success" : "attention"}>
                    {value ? translate("Đã xử lý") : translate("Chưa xử lý")}
                  </Label>
                </Box>
              )}
            />
          </Box>

          {!daXuLy ? (
            <Box sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
              py: 4,
              fontSize: 1,
              color: "fg.muted",
              bg: "canvas.default",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "border.default",
            }}>
              {translate("Đánh dấu đã xử lý để nhập ngày và kết quả xử lý.")}
            </Box>
          ) : (
            <>
              <Controller
                name="ngay_xu_ly"
                control={control}
                rules={{ required: "Vui lòng chọn ngày xử lý" }}
                render={({ field }) => (
                  <FormControl>
                    <FormControl.Label>{translate("Ngày xử lý")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
                    <DateInputField
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ariaLabel="Ngày xử lý"
                      error={errors.ngay_xu_ly?.message}
                    />
                  </FormControl>
                )}
              />
              <Controller
                name="ket_qua_xu_ly"
                control={control}
                rules={{ required: "Vui lòng nhập kết quả xử lý" }}
                render={({ field }) => (
                  <FormControl sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <FormControl.Label>{translate("Kết quả xử lý")}<Box as="span" sx={{ color: "danger.fg" }}> *</Box></FormControl.Label>
                    <TextArea
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ariaLabel="Kết quả xử lý"
                      minHeight="160px"
                      hasError={!!errors.ket_qua_xu_ly}
                      grow
                    />
                    {fieldError(errors.ket_qua_xu_ly?.message)}
                  </FormControl>
                )}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PhuHuynhCSKHDetailModal;
